import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, Star } from "lucide-react";
import { gardeMeubles } from "../mock/extraData";
import Reveal from "../components/Reveal";

const GardeMeubles = () => {
  const navigate = useNavigate();
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="pt-16 sm:pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={gardeMeubles.hero.image} alt="Garde-meubles" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#131f38]/95 to-[#1B2A4A]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28">
          <div className="max-w-2xl animate-fadeUp">
            <span className="inline-block bg-[#C9A227] text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">Garde-meubles</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">{gardeMeubles.hero.title}</h1>
            <p className="mt-5 text-lg text-gray-200 max-w-xl">{gardeMeubles.hero.subtitle}</p>
            <button onClick={() => navigate("/contact")} className="mt-8 inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors shadow-lg">
              Réserver un box <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#1B2A4A] mb-5">Vos biens en sécurité, l'esprit tranquille</h2>
            {gardeMeubles.intro.map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed mb-4">{p}</p>
            ))}
          </Reveal>
          <Reveal delay={150}>
            <img src={gardeMeubles.image} alt="Box de stockage" className="rounded-2xl shadow-xl w-full h-[360px] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Avantages</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2">Pourquoi nos garde-meubles ?</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gardeMeubles.features.map((f, i) => {
              const Icon = Icons[f.icon] || Icons.ShieldCheck;
              return (
                <Reveal key={i} delay={(i % 3) * 100} className="group bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-[#C9A227]/40 transition-all duration-300">
                  <div className="w-14 h-14 rounded-xl bg-[#1B2A4A]/5 group-hover:bg-[#1B2A4A] flex items-center justify-center transition-colors duration-300">
                    <Icon size={26} className="text-[#1B2A4A] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-[#1B2A4A]">{f.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BOX SIZES */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Nos box</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2 mb-3">Choisissez la taille adaptée</h2>
            <p className="text-gray-600">Tarifs indicatifs — devis personnalisé selon la durée et le volume.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {gardeMeubles.boxes.map((b, idx) => (
              <Reveal key={idx} delay={idx * 100} className={`relative bg-white rounded-2xl p-6 border text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${b.popular ? "border-[#C9A227] shadow-lg ring-1 ring-[#C9A227]/30" : "border-gray-100 shadow-sm"}`}>
                {b.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A227] text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star size={12} className="fill-white" /> Populaire
                  </span>
                )}
                <div className="text-3xl font-extrabold text-[#1B2A4A]">{b.size}</div>
                <div className="text-sm font-semibold text-[#C9A227] mt-1">{b.label}</div>
                <p className="text-sm text-gray-600 mt-3 min-h-[40px]">{b.desc}</p>
                <div className="mt-4 font-bold text-[#1B2A4A]">{b.price}</div>
                <button onClick={() => navigate("/contact")} className={`mt-5 w-full font-semibold py-2.5 rounded-lg transition-colors ${b.popular ? "bg-[#1B2A4A] hover:bg-[#26406b] text-white" : "border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white"}`}>
                  Réserver
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Besoin d'un espace de stockage ?</h2>
          <p className="text-gray-300 mb-8">Contactez-nous pour connaître les disponibilités et obtenir un devis gratuit.</p>
          <button onClick={() => navigate("/contact")} className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors shadow-lg">
            Demander un devis <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default GardeMeubles;
